import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Modal, Tab, Tabs} from 'react-bootstrap';
import {ToastContainer} from 'react-toastify';
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent';
import {Column, Metric, Numeric, ReportTable, kg, tonnes} from './InventuraReportComponents';
import LokacijeRepromaterijalaDropdown from '../../components/LokacijeRepromaterijalaDropdown/LokacijeRepromaterijalaDropdown';
import notifyToast from '../../components/Toast/Toast';
import {getSumaRolniInventurePremaFmkSifri, izvjestajPremaLokaciji,
    izvjestajZaInventuruRolniV2, podesiNoveLokacijeRolni, fetchInventura,
    zakljuciInventuruRolni, ZakljuciInventuruRolniPayload, INVENTURE_UPDATED_EVENT} from '../../api/inventure';
import {InventureModel} from '../../models/inventure';

interface ZadnjiPromet {
    zadnjiPrometId?: Numeric | null;
    uposlenik?: {id: Numeric; imePrezime: string | null} | null;
}
interface NeskeniranaRolna extends ZadnjiPromet {
    tezinaKg: Numeric;
    ReprMatNettoKolicina?: Numeric | null;
    ReprMatLokacija?: Numeric | null;
}
interface Rolna extends ZadnjiPromet {
    RolnaId: Numeric;
    Tezina: Numeric;
    SkeniranaTezina: Numeric;
    Lokacija?: string | null;
    SkeniranaLokacija?: string | null;
    SkeniranaLokacijaRed?: Numeric | null;
}
interface TrenutnaRolna extends Rolna {
    trenutnaTezinaKg?: Numeric | null;
    tezinaUkljucenaUTrenutnoStanjeKg: Numeric;
    razlikaKg: Numeric;
    razlog: string;
}
interface ReportData {
    skeniraneRolne: {brojRolni: Numeric; tezinaKg: Numeric};
    evidentiranaTezinaSkeniranihRolniKg: Numeric;
    razlikaSkeniraneIEvidentiraneTezineKg: Numeric;
    brojNeskeniranihRolni: Numeric;
    tezinaNeskeniranihRolniKg: Numeric;
    neskeniraneRolneNaTrenutnomStanju: Record<string, NeskeniranaRolna | Numeric>;
    pronadjeneRolneBezEvidentiraneTezine: Rolna[];
    tezinaRolniBezEvidentiraneTezineKg: Numeric;
    rolneSaOdstupanjemTezine: Rolna[];
    brojRolniSaOdstupanjemTezine: Numeric;
    razlikaTezineEvidentiranihRolniKg: Numeric;
    rolneSaOdstupanjemLokacije: Rolna[];
    brojRolniSaOdstupanjemLokacije: Numeric;
    duplaSkeniranja: {RolnaId: Numeric; stavkeIds: Numeric[]; koristenaStavkaId: Numeric}[];
    poredjenjeSaTrenutnimStanjemSkladista: {
        trenutnoStanjeSkladista: {brojRolni: Numeric; tezinaKg: Numeric};
        skeniranaPlusNeskeniranaTezinaKg: Numeric;
        razlikaKg: Numeric;
        rolneSaOdstupanjemOdTrenutnogStanja: TrenutnaRolna[];
    };
}
const validNumber = (value: unknown): boolean =>
    (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && Number.isFinite(Number(value));
const closingPayload = (id: number, report: ReportData | null): ZakljuciInventuruRolniPayload | null => {
    const values = {
        IdFromURL: id,
        stanjeLageraKG: report?.poredjenjeSaTrenutnimStanjemSkladista?.trenutnoStanjeSkladista?.tezinaKg,
        stanjeLageraKom: report?.poredjenjeSaTrenutnimStanjemSkladista?.trenutnoStanjeSkladista?.brojRolni,
        tezinaRolniPrilikomSkeniranja: report?.skeniraneRolne?.tezinaKg,
        tezinaRolniNakonSkeniranja: report?.evidentiranaTezinaSkeniranihRolniKg,
        razlikaInventure: report?.razlikaSkeniraneIEvidentiraneTezineKg,
        rolneNisuPronadjeneKomada: report?.brojNeskeniranihRolni,
        rolneNisuPronadjeneKG: report?.tezinaNeskeniranihRolniKg,
    };
    if (!Number.isInteger(id) || id <= 0 || !Object.values(values).every(validNumber)) return null;
    const neskenirane = report?.neskeniraneRolneNaTrenutnomStanju;
    if (!neskenirane || typeof neskenirane !== 'object' || Array.isArray(neskenirane)) return null;
    const entries = Object.entries(neskenirane);
    if (!entries.every(([, stavka]) => stavka && typeof stavka === 'object' && validNumber(stavka.tezinaKg))) return null;
    const rolneNisuPronadjene = Object.fromEntries(entries.map(([rolnaId, stavka]) =>
        [rolnaId, Number((stavka as NeskeniranaRolna).tezinaKg)]));
    return {
        ...Object.fromEntries(Object.entries(values).map(([key, value]) => [key, Number(value)])),
        rolneNisuPronadjene,
    } as ZakljuciInventuruRolniPayload;
};
const reasonLabels: Record<string, string> = {
    razlicita_tezina: 'Razlika prema trenutnom stanju',
    nije_na_trenutnom_stanju: 'Nema pozitivnog trenutnog stanja',
};
const errorText = (error: any): string => {
    const body = error?.response?.data ?? error;
    const details = body?.errors ?? body?.data?.errors;
    const invalid = body?.data?.neispravneStavke ?? body?.neispravneStavke;
    return [body?.message || 'Greška prilikom učitavanja podataka.',
        details && JSON.stringify(details, null, 2),
        invalid && 'Neispravne stavke: ' + JSON.stringify(invalid, null, 2),
    ].filter(Boolean).join('\n');
};
const zadnjiPrometColumn: Column<ZadnjiPromet> = {
    title: 'Zadnji promet', render: row => row.uposlenik?.imePrezime || '—',
};
const weightColumns: Column<Rolna>[] = [
    {title: 'ID rolne', render: row => row.RolnaId},
    {title: 'Težina rolne (kg)', render: row => kg(row.Tezina), totalValue: row => Number(row.Tezina)},
    {title: 'Skenirana težina (kg)', render: row => kg(row.SkeniranaTezina), totalValue: row => Number(row.SkeniranaTezina)},
];
const InventureRolneIzvjestaj: React.FC = () => {
    const {id} = useParams();
    const IdFromURL = Number(id);
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [relatedError, setRelatedError] = useState('');
    const [repairing, setRepairing] = useState(false);
    const [inventura, setInventura] = useState<Partial<InventureModel> | null>(null);
    const [inventoryLoading, setInventoryLoading] = useState(true);
    const [inventoryError, setInventoryError] = useState('');
    const [closing, setClosing] = useState(false);
    const [closingError, setClosingError] = useState('');
    const [closedId, setClosedId] = useState<number | null>(null);
    const closingLock = useRef(false);
    const inventoryRequest = useRef(0);
    const repairLock = useRef(false);
    const reportRequest = useRef(0);
    const locationRequest = useRef(0);
    const [lokacijaRolne, setLokacijaRolne] = useState('');
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationRows, setLocationRows] = useState<{RolnaId: Numeric}[]>([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState('');

    const fetchInventory = useCallback(async () => {
        const request = ++inventoryRequest.current;
        setInventoryLoading(true);
        setInventura(null);
        setInventoryError('');
        try {
            if (!Number.isInteger(IdFromURL) || IdFromURL <= 0) throw new Error('Neispravan broj inventure.');
            const result = await fetchInventura(IdFromURL);
            if (request === inventoryRequest.current) setInventura(result);
        } catch (failure) {
            if (request === inventoryRequest.current) setInventoryError(errorText(failure));
        } finally {
            if (request === inventoryRequest.current) setInventoryLoading(false);
        }
    }, [IdFromURL]);
    useEffect(() => {
        setClosingError('');
        void fetchInventory();
        return () => {inventoryRequest.current++;};
    }, [fetchInventory]);

    const fetchReport = useCallback(async () => {
        const request = ++reportRequest.current;
        setLoading(true);
        setReportData(null);
        setError('');
        try {
            if (!Number.isInteger(IdFromURL) || IdFromURL <= 0) throw new Error('Neispravan broj inventure.');
            const response = await izvjestajZaInventuruRolniV2({IdFromURL});
            const body = response.data;
            if ((body.error != null && Number(body.error) !== 0) || body.success === false ||
                !body.data?.skeniraneRolne || !body.data?.poredjenjeSaTrenutnimStanjemSkladista?.trenutnoStanjeSkladista) throw body;
            if (request === reportRequest.current) setReportData(body.data);
        } catch (failure) {
            if (request === reportRequest.current) setError(errorText(failure));
        } finally {
            if (request === reportRequest.current) setLoading(false);
        }
    }, [IdFromURL]);
    const fetchLocation = useCallback(async (location: string) => {
        const request = ++locationRequest.current;
        setLocationRows([]);
        setLocationError('');
        setLocationLoading(Boolean(location));
        if (!location) return;
        try {
            const response = await izvjestajPremaLokaciji({lokacija: location, idInventure: IdFromURL});
            if (response?.error != null && Number(response.error) !== 0) throw response;
            const rows = Object.values(response ?? {}).filter(value =>
                (typeof value === 'number' || typeof value === 'string') && Number.isFinite(Number(value))
            ).map(value => ({RolnaId: value as Numeric}));
            if (request === locationRequest.current) setLocationRows(rows);
        } catch (failure) {
            if (request === locationRequest.current) setLocationError(errorText(failure));
        } finally {
            if (request === locationRequest.current) setLocationLoading(false);
        }
    }, [IdFromURL]);
    const fetchRelated = useCallback(async () => {
        setRelatedError('');
        try {
            const response = await getSumaRolniInventurePremaFmkSifri({IdFromURL});
            if (response?.error != null && Number(response.error) !== 0) throw response;
        } catch (failure) {setRelatedError(errorText(failure));}
    }, [IdFromURL]);
    useEffect(() => {
        void fetchReport();
        if (Number.isInteger(IdFromURL) && IdFromURL > 0) void fetchRelated();
        return () => {reportRequest.current++;};
    }, [IdFromURL, fetchReport, fetchRelated]);
    useEffect(() => {
        void fetchLocation(lokacijaRolne);
        return () => {locationRequest.current++;};
    }, [fetchLocation, lokacijaRolne]);
    const refresh = async () => {
        await Promise.all([fetchReport(), fetchRelated(), fetchLocation(lokacijaRolne), fetchInventory()]);
    };
    const podesiNoveLokacije = async () => {
        if (repairLock.current || closingLock.current) return;
        repairLock.current = true;
        setRepairing(true);
        try {
            // Preserve the existing inventory-wide action and its exact parameters.
            const response = await podesiNoveLokacijeRolni({IdFromURL});
            if (response.error === 0) {
                notifyToast(true, response.message || 'Lokacije su uspješno ažurirane.');
                await refresh();
            } else notifyToast(false, response.message || 'Popravka lokacija nije uspjela.');
        } catch (failure) {notifyToast(false, errorText(failure));}
        finally {repairLock.current = false; setRepairing(false);}
    };
    const comparison = reportData?.poredjenjeSaTrenutnimStanjemSkladista;
    const payload = closingPayload(IdFromURL, reportData);
    const isClosed = closedId === IdFromURL || (Number(inventura?.Id) === IdFromURL && Number(inventura?.Status) === 37);
    const canClose = !isClosed && !loading && !inventoryLoading && !repairing && !closing && !!payload &&
        Number(inventura?.Id) === IdFromURL && Number(inventura?.Status) === 36 && !inventura?.Kraj;
    const zakljuciInventuru = async () => {
        if (!canClose || !payload || closingLock.current || repairLock.current) return;
        if (!window.confirm('Želite li zaključiti inventuru? Rezultati će biti sačuvani i status promijenjen u Zaključena.')) return;
        closingLock.current = true;
        setClosing(true);
        setClosingError('');
        try {
            const response = await zakljuciInventuruRolni(payload);
            if ((response?.error != null && Number(response.error) !== 0) || response?.success === false) throw response;
            setClosedId(IdFromURL);
            notifyToast(true, 'Inventura je uspješno zaključena');
            window.dispatchEvent(new Event(INVENTURE_UPDATED_EVENT));
            await fetchInventory();
        } catch (failure: any) {
            setClosingError(errorText(failure));
            if (failure?.response?.status === 409) {
                window.dispatchEvent(new Event(INVENTURE_UPDATED_EVENT));
                await fetchInventory();
            }
        } finally {
            closingLock.current = false;
            setClosing(false);
        }
    };
    return <>
        <ToastContainer />
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-6'>
            <h2>Inventura #{id} — izvještaj rolni</h2>
            <div className='d-flex flex-wrap gap-3'>
                <button type='button' className='btn btn-light-primary' disabled={loading || repairing || closing || inventoryLoading || locationLoading}
                    onClick={() => void refresh()}>Osvježi</button>
                <button type='button' className='btn btn-primary' disabled={loading || repairing || closing || !reportData}
                    onClick={() => void podesiNoveLokacije()}>{repairing ? 'Popravka u toku…' : 'PODESI NOVE LOKACIJE'}</button>
                <button type='button' className='btn btn-success' disabled={!canClose}
                    onClick={() => void zakljuciInventuru()}>
                    {closing ? 'Zaključivanje…' : isClosed ? 'Inventura zaključena' : 'Zaključi inventuru'}
                </button>
            </div>
        </div>
        {inventura && <p>Status inventure: {Number(inventura.Status) === 37 ? 'Zaključena' : Number(inventura.Status) === 36 ? 'Aktivna' : inventura.Status}
            {inventura.Kraj && <> · Kraj: {inventura.Kraj}</>}</p>}
        {inventoryError && <div className='alert alert-danger' role='alert'>Status inventure nije učitan: {inventoryError}</div>}
        {closingError && <div className='alert alert-danger' role='alert' style={{whiteSpace: 'pre-wrap'}}>{closingError}</div>}
        {!loading && reportData && !payload && <p className='alert alert-warning'>Zaključivanje nije moguće jer nedostaju obavezni numerički podaci izvještaja.</p>}
        <p className='alert alert-info'>Težine i lokacije inventure sačuvane su prilikom skeniranja.
            Neskenirane rolne koriste trenutno stanje skladišta.</p>
        {loading && <LoadingComponent />}
        {error && <div className='alert alert-danger' role='alert' style={{whiteSpace: 'pre-wrap'}}>{error}</div>}
        {relatedError && <div className='alert alert-warning' role='alert'>Povezani podaci po FMK šifri: {relatedError}</div>}
        {!loading && reportData && <>
            <div className='row'>
                <Metric title='Skenirane rolne' value={<>{reportData.skeniraneRolne.brojRolni} <span className='fs-3'>kom</span></>} valueClassName='display-4 fw-bolder text-success' />
                <Metric title='Skenirana težina' value={kg(reportData.skeniraneRolne.tezinaKg)} tonnesValue={tonnes(reportData.skeniraneRolne.tezinaKg)}
                    description='Ukupna težina rolni tokom skeniranja rolni na ovoj inventuri. Npr rolna prilikom skeniranja imala na stanju 500 a nakon skeniranja i unosa tezina imala 700 ili manje' />
                <Metric title='Težina skeniranih rolni' value={kg(reportData.evidentiranaTezinaSkeniranihRolniKg)} tonnesValue={tonnes(reportData.evidentiranaTezinaSkeniranihRolniKg)}
                    description='Trenutna težina rolne u sistemu za vrijeme skeniranja. Prikazan je zbir za sve skenirane rolne.' />
                <Metric title='Razlika inventure' value={kg(reportData.razlikaSkeniraneIEvidentiraneTezineKg)}
                    description='Skenirana težina − težina skeniranih rolni u sistemu pri skeniranju. Razlika je prikazana u kilogramima (1 t = 1.000 kg). Pozitivna vrijednost znači da je uneseno više, a negativna da je uneseno manje od evidentirane težine.' />
                <Metric title='Neskenirane rolne' value={<>{reportData.brojNeskeniranihRolni} <span className='fs-3'>kom</span></>} valueClassName='display-4 fw-bolder text-danger' />
                <Metric title='Težina neskeniranih' value={kg(reportData.tezinaNeskeniranihRolniKg)} tonnesValue={tonnes(reportData.tezinaNeskeniranihRolniKg)} />
            </div>
            <p>Pozitivna razlika znači da je skenirano više od evidentirane težine, a negativna da je skenirano manje.</p>
            <div className='card mb-6'><div className='card-body'>
                <Tabs id='inventura-rolne-tabs' defaultActiveKey='neskenirane' mountOnEnter unmountOnExit>
                    <Tab eventKey='neskenirane' title='Neskenirane rolne'>
                        <p className='mt-4'>Neskenirano ne znači automatski fizički manjak.</p>
                        <ReportTable<NeskeniranaRolna & {RolnaId: Numeric}> showTotals label='Neskenirane rolne' rows={Object.entries(reportData.neskeniraneRolneNaTrenutnomStanju ?? {})
                            .map(([RolnaId, value]) => typeof value === 'object'
                                ? {...value, RolnaId}
                                : {RolnaId, tezinaKg: value, uposlenik: null})} columns={[
                            {title: 'ID rolne', render: row => validNumber(row.ReprMatNettoKolicina) && validNumber(row.tezinaKg)
                                && Number(row.ReprMatNettoKolicina) === Number(row.tezinaKg)
                                ? <strong className='text-danger fw-bold'>{row.RolnaId}</strong> : row.RolnaId},
                            {title: 'Netto količina (kg)', render: row => kg(row.ReprMatNettoKolicina)},
                            {title: 'Trenutna težina (kg)', render: row => kg(row.tezinaKg), totalValue: row => Number(row.tezinaKg)},
                            {title: 'Lokacija', render: row => row.ReprMatLokacija ?? '—'},
                            zadnjiPrometColumn,
                        ]} />
                    </Tab>
                    <Tab eventKey='pronadjene' title='Pronađene rolne (stanje bilo 0)'>
                        <p className='mt-4'>Evidentirana težina je 0, a skenirana veća od 0. Zbir: {kg(reportData.tezinaRolniBezEvidentiraneTezineKg)}</p>
                        <ReportTable showTotals label='Pronađene bez evidentirane težine' rows={reportData.pronadjeneRolneBezEvidentiraneTezine ?? []}
                            columns={[...weightColumns, {title: 'Skenirana lokacija', render: row => row.SkeniranaLokacija ?? '—'}, zadnjiPrometColumn]} />
                    </Tab>
                    <Tab eventKey='tezine' title='Greska u težini'>
                        <p className='mt-4'>Broj: {reportData.brojRolniSaOdstupanjemTezine}. Ukupna razlika: {kg(reportData.razlikaTezineEvidentiranihRolniKg)}</p>
                        <ReportTable showTotals label='Odstupanja težine' rows={reportData.rolneSaOdstupanjemTezine ?? []}
                            columns={[...weightColumns, {title: 'Razlika (kg)', render: row => kg(Number(row.SkeniranaTezina) - Number(row.Tezina)),
                                totalValue: row => Number(row.SkeniranaTezina) - Number(row.Tezina)}, zadnjiPrometColumn]} />
                    </Tab>
                    <Tab eventKey='lokacije' title='Odstupanja lokacije'>
                        <p className='mt-4'>Broj: {reportData.brojRolniSaOdstupanjemLokacije}. Lokacija označava evidentiranu lokaciju prilikom skeniranja.
                            Sačuvane lokacije mogu ostati različite i nakon popravke trenutnog skladišta.</p>
                        <ReportTable label='Odstupanja lokacije' rows={reportData.rolneSaOdstupanjemLokacije ?? []} columns={[
                            {title: 'ID rolne', render: row => row.RolnaId},
                            {title: 'Evidentirana lokacija', render: row => row.Lokacija ?? '—'},
                            {title: 'Skenirana lokacija', render: row => row.SkeniranaLokacija ?? '—'},
                            {title: 'Skenirani red lokacije', render: row => row.SkeniranaLokacijaRed ?? '—'},
                        ]} />
                    </Tab>
                    <Tab eventKey='dupla' title='Dupla skeniranja'>
                        <p className='mt-4'>U zbir se uključuje samo stavka s najvećim ID-em.</p>
                        <ReportTable label='Dupla skeniranja' rows={reportData.duplaSkeniranja ?? []} columns={[
                            {title: 'ID rolne', render: row => row.RolnaId},
                            {title: 'ID-evi stavki', render: row => row.stavkeIds.join(', ')},
                            {title: 'Korištena stavka ID', render: row => row.koristenaStavkaId},
                        ]} />
                    </Tab>
                </Tabs>
            </div></div>
            {comparison && <section className='mb-6' aria-label='Poređenje s trenutnim skladištem'>
                <h3>Poređenje s trenutnim skladištem</h3>
                <p>Ova odstupanja predstavljaju poređenje s trenutnim stanjem skladišta i nisu greške težine iz inventure.
                    Razlika poredi skeniranu težinu s težinom uključenom u trenutno stanje.</p>
                <div className='row'>
                    <Metric title='Trenutno stanje — broj rolni' value={<>{comparison.trenutnoStanjeSkladista.brojRolni} <span className='fs-3'>kom</span></>} valueClassName='display-4 fw-bolder text-success' />
                    <Metric title='Trenutno stanje — težina rolni na lageru' value={kg(comparison.trenutnoStanjeSkladista.tezinaKg)} tonnesValue={tonnes(comparison.trenutnoStanjeSkladista.tezinaKg)} />
                    <Metric title='Skenirano + neskenirano' value={kg(comparison.skeniranaPlusNeskeniranaTezinaKg)} tonnesValue={tonnes(comparison.skeniranaPlusNeskeniranaTezinaKg)} />
                    <Metric title='Razlika prema trenutnom stanju' value={kg(comparison.razlikaKg)} />
                </div>
                <div className='card'><div className='card-body'>
                    <ReportTable<TrenutnaRolna> label='Odstupanja od trenutnog stanja' rows={comparison.rolneSaOdstupanjemOdTrenutnogStanja ?? []} columns={[
                        {title: 'ID rolne', render: row => row.RolnaId},
                        {title: 'Evidentirano prilikom skeniranja (kg)', render: row => kg(row.Tezina)},
                        {title: 'Skenirana težina (kg)', render: row => kg(row.SkeniranaTezina)},
                        {title: 'Trenutna težina (kg)', render: row => row.trenutnaTezinaKg == null ? 'Nema zapisa' : kg(row.trenutnaTezinaKg)},
                        {title: 'Razlika (kg)', render: row => kg(row.razlikaKg)},
                        {title: 'Razlog', render: row => reasonLabels[row.razlog] ?? row.razlog},
                    ]} />
                </div></div>
            </section>}
        </>}
        <section className='card mb-6'><div className='card-body'>
            <h3>Pregled po lokaciji — neskenirane rolne</h3>
            <p className='text-muted'>Otvorite pregled i odaberite lokaciju za prikaz neskeniranih rolni.</p>
            <button type='button' className='btn btn-light-primary' onClick={() => setShowLocationModal(true)}>
                Pregled po lokaciji
            </button>
        </div></section>
        <Modal show={showLocationModal} onHide={() => setShowLocationModal(false)} size='xl'
            fullscreen='md-down' centered scrollable aria-labelledby='location-report-title'>
            <Modal.Header closeButton closeLabel='Zatvori'>
                <Modal.Title id='location-report-title'>Inventura #{id} — pregled po lokaciji</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h4>Odaberite lokaciju</h4>
                <LokacijeRepromaterijalaDropdown value={lokacijaRolne} selectName='LokacijaRolne'
                    onChange={(_name, value) => setLokacijaRolne(String(value ?? ''))} />
                <p className='text-muted mt-4'>Neskenirane rolne na odabranoj lokaciji. Neskenirano ne znači automatski fizički manjak.</p>
                {locationLoading ? <LoadingComponent /> : locationError ?
                    <div className='alert alert-danger mt-4' role='alert'>{locationError}</div> : lokacijaRolne ?
                    <div className='mt-4'><ReportTable key={lokacijaRolne} label='Neskenirane rolne odabrane lokacije' rows={locationRows}
                        columns={[{title: 'ID rolne', render: row => row.RolnaId}]} /></div> :
                    <p className='mt-4'>Odaberite lokaciju za pregled.</p>}
            </Modal.Body>
            <Modal.Footer>
                <button type='button' className='btn btn-light' onClick={() => setShowLocationModal(false)}>Zatvori</button>
            </Modal.Footer>
        </Modal>
    </>;
};
export default InventureRolneIzvjestaj;
